import { Dispatch, useState, useCallback, useMemo, useEffect } from 'react';
import { useToggle } from '../../core/hooks/useToggle';
import { useAppState } from '../../core/redux/hooks';
import { sendEQScoresAction } from './api/sendEQScoresAction';
import { SendEQScoresModalProps, Report, EQScores } from './models';
import { getEQScoresAction } from './api/getEQScoresAction';
import { useSessionStorage } from '../../core/hooks/useSessionStorage';
import { SessionStorageKeys } from '../../localStorage/sessionStorageKeys';
import { useRoutes } from '../../navigation/routes';
import { useCurrentUser } from '../../auth/hooks';
import { useMount } from '../../core/hooks/useMount';
import { ProjectCSContact } from '../models';
import { getProjectCSContactAction } from '../api/getProjectCSContactAction';


export const useSendEQScoresModalProps = (
  projId: number,
  participantId: number,
  dispatch: Dispatch<any>,
): SendEQScoresModalProps => {
  // If collaboration is enabled, rather than immediately invite a collaborator when pressing the send-email button, we
  // raise a modal to inform the user of the data sharing ramifications. If they agree, we'll use the collected email
  // address and comment text to send the prospective collaborator an invite.
  const [comment, setComment]               = useState<string | undefined>('');
  const [recipientEmail, setRecipientEmail] = useState<string | undefined>();
  const [currentReport, setCurrentReport]   = useState<Report | undefined>();
  const currentReleaseId                    = useMemo(() => currentReport?.releaseId ?? -1, [currentReport]);

  const [modalVisible, toggleModal] = useToggle();

  const proj = useAppState(state => state.projectSummary);
  const logo = useMemo(() => proj?.clientImageUrl, [proj]);

  const csContact = useAppState(state => state.projectCSContacts[`${projId}`]);

  const participant = useCurrentUser();

  const [, setCustomEmailTxt] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.EQScoresEmailTxt}-${currentReleaseId}`,
  ); //<@ExtraText>;

  const [, setReportScores] = useSessionStorage<EQScores | undefined>(
    `${SessionStorageKeys.EQScores}-${currentReleaseId}`,
  ); //<@cEQScore>, etc;

  const [, setCustomEmailLogo] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailLogo}-${projId}`,
  );  //<@PrimaryLogo>

  const [, setCustomEmailParticipant] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailParticipant}-${projId}`,
  ); //<@cRateeName>

  const [, setProjectCSContact] = useSessionStorage<ProjectCSContact | undefined>(
    `${SessionStorageKeys.ProjectCSContact}-${projId}`,
  ); //<@cClientSupportFirstLast>, <@cClientSupportEmail>

  const routes = useRoutes(projId, participantId);

  const sendEQScoresEmailPreviewRoute = useMemo(
    () => routes.SendEQScoresEmailPreview + `#${projId}#${currentReleaseId}`,
    [currentReleaseId, projId, routes.SendEQScoresEmailPreview],
  );

  const eqScores = useAppState(state => state.eqScores?.[`${currentReleaseId}`]);

  useMount(
    () => {
      setCustomEmailLogo(logo);
      setCustomEmailParticipant(participant?.personName ?? '');
    },
  );

  useEffect(
    () => {
      // Set dirty comment state into session storage for iFrame to read out
      setCustomEmailTxt(comment?.replace(/\n/g, '<br/>'));

      // Return cleanup function to clear out dirty state onUnmount
      return () => setCustomEmailTxt(undefined);
    },
    [setCustomEmailTxt, comment],
  );

  useEffect(
    () => {
      if (currentReleaseId < 0) {
        return;
      }

      if (!eqScores) {
        return;
      }

      setReportScores(eqScores);
    },
    [currentReleaseId, eqScores, setReportScores],
  );

  useEffect(
    () => {
      if (!!csContact) {
        setProjectCSContact(csContact);
      }
    },
    [csContact, setProjectCSContact],
  );

  const {
          sendEQScoresModalVisible,
          showSendEQScoresModal,
          dismissSendEQScoresModal,
          setEQScoresRecipientEmail,
          setEQScoresRecipientComment,
        } = useMemo(
    () => ({
      sendEQScoresModalVisible:    modalVisible,
      showSendEQScoresModal:       (report: Report) => {
        dispatch(getProjectCSContactAction(projId));
        dispatch(getEQScoresAction(participantId, report.releaseId));
        setCurrentReport(report);
        toggleModal();
      },
      dismissSendEQScoresModal:    toggleModal,
      setEQScoresRecipientEmail:   (email: string) => setRecipientEmail(email),
      setEQScoresRecipientComment: (comment: string) => setComment(comment),
    }),
    [dispatch, modalVisible, participantId, projId, toggleModal],
  );

  const langId = useAppState(state => state.locale.lang.languageId);

  const onConfirmShareEQScores = useCallback(
    () => {
      if (!recipientEmail) {
        return;
      }

      dispatch(sendEQScoresAction(
        projId,
        participantId,
        recipientEmail,
        comment ?? '',
        langId,
        currentReport!.releaseId, // We'll never get this far without a report set
      ));

      toggleModal();
    },
    [comment, dispatch, langId, participantId, projId, recipientEmail, currentReport, toggleModal],
  );

  return {
    sendEQScoresModalVisible,
    sendEQScoresEmailPreviewRoute,
    showSendEQScoresModal,
    dismissSendEQScoresModal,
    setEQScoresRecipientComment,
    setEQScoresRecipientEmail,
    onConfirmShareEQScores,
  };
};
